import React from 'react';
import { handleRedirect } from '@/lib/iframeRedirect';
import * as m from '@/paraglide/messages.js';
import { Money } from '@/lib/money';

// Minimal surface of pay.nl's PAY.PARTS SDK — only what we call. Typed here rather than pulled in
// as a dependency because pay.nl ships the SDK as a script, not a package.
// https://developer.pay.nl/docs/add-components-to-your-frontend
interface PayPartsComponent {
    bind: (selector: string) => Promise<void>;
}

interface PayPartsEvents {
    onSuccess?: (event: { resolve?: () => void; orderId?: string; transaction?: { transactionId?: string } }) => void | Promise<void>;
    onError?: (event: { resolve?: () => void; message?: string }) => void | Promise<void>;
    onPartialPaid?: (event: PartialPaidEvent) => void;
}

interface PayPartsCheckoutConfig {
    amount?: number;
    excludedMethods?: string[];
    checkoutOptions?: { id: number; tag: string; name: string }[];
}

interface PayPartsCheckout {
    prepare: (component: string) => PayPartsComponent;
    // Assigned AFTER init — init() ignores handlers passed in its options. See where this is set.
    events: PayPartsEvents;
    getConfig: () => PayPartsCheckoutConfig;
}

// What the SDK hands onPartialPaid. Undocumented, so taken from the shipped bundle: it passes
// { orderId, amountPaid, amountRemaining, currency, transactionId, resolve, reject }.
interface PartialPaidEvent {
    amountRemaining?: number;
    currency?: string;
    resolve?: () => void;
}

interface PayPartsSdk {
    // Config only. The SDK's init() destructures a fixed set of config keys and silently drops
    // anything else — event handlers included. They go on checkout.events afterwards.
    init: (options: {
        sessionToken: string;
        apiUrl: string;
        country?: string;
        language?: string;
        excludedMethods?: string[];
        debugMode?: boolean;
    }) => Promise<PayPartsCheckout>;
}

declare global {
    interface Window {
        PayPartsSDK?: PayPartsSdk;
    }
}

// The SDK is a module script and must only be added once per page — the widget can mount and
// unmount checkout repeatedly (method switch, back navigation) without reloading the document.
const loadSdk = (() => {
    const loaders = new Map<string, Promise<void>>();

    return (src: string): Promise<void> => {
        if (window.PayPartsSDK) return Promise.resolve();

        const existing = loaders.get(src);
        if (existing) return existing;

        const loader = new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.type = 'module';
            script.src = src;
            script.onload = () => resolve();
            script.onerror = () => {
                // Let a later attempt retry rather than caching the failure forever.
                loaders.delete(src);
                reject(new Error('Could not load the payment component'));
            };
            document.head.appendChild(script);
        });

        loaders.set(src, loader);
        return loader;
    };
})();

interface EmbeddedPaymentProps {
    sessionToken: string;
    component: string;
    sdkUrl: string;
    apiUrl: string;
    returnUrl: string;
    languageCode?: string;
    // Reports the completed payment to our API as a fallback for a lost pay.nl exchange, then
    // hands off to the pending-payment UI without leaving the page.
    onPaid?: (orderId: string | null) => void | Promise<void>;
    // Called when the component cannot be mounted at all, so the caller can retry the payment on
    // the hosted redirect flow rather than stranding the buyer on a form that will never work.
    onMountFailed?: () => void;
    // Lets the buyer go back and pick a different method — the session is bound to the method they
    // chose, so switching means starting checkout again rather than re-binding this component.
    onCancel?: () => void;
}

/**
 * Renders the pay.nl component for the method the buyer already selected, inside our own checkout.
 *
 * Only one component is ever mounted: the API fixed the session amount (including that method's
 * fee) when it started the session, so letting the buyer switch method here would silently
 * invalidate the amount they agreed to.
 */
export function EmbeddedPayment({
    sessionToken,
    component,
    sdkUrl,
    apiUrl,
    returnUrl,
    languageCode,
    onCancel,
    onPaid,
    onMountFailed,
}: EmbeddedPaymentProps) {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [ready, setReady] = React.useState(false);

    // Set once a giftcard covers only part of the order. Holds what is still owed so the buyer can
    // see it, and switches this component into "collect the rest" mode.
    const [remaining, setRemaining] = React.useState<{ amount: number; currency: string } | null>(null);

    // The live checkout handle. Kept so the partial-payment path can mount a second component into
    // the same session — the SDK tracks the outstanding amount itself, so whatever is mounted next
    // charges the remainder rather than the original total.
    const checkoutRef = React.useRef<PayPartsCheckout | null>(null);

    // Held in a ref, not a dependency: the callback is defined inline by the parent and so changes
    // identity every render. In the dependency array it would tear down and re-create the pay.nl
    // session on each one; leaving it out entirely would capture a stale closure.
    const onPaidRef = React.useRef(onPaid);
    onPaidRef.current = onPaid;

    // Same reasoning as onPaidRef: defined inline by the parent, so it must not sit in the
    // dependency array or every render would tear down and re-create the pay.nl session.
    const onMountFailedRef = React.useRef(onMountFailed);
    onMountFailedRef.current = onMountFailed;

    React.useEffect(() => {
        let cancelled = false;

        const mount = async () => {
            try {
                await loadSdk(sdkUrl);

                if (cancelled) return;

                const sdk = window.PayPartsSDK;
                if (!sdk) {
                    throw new Error('Payment component unavailable');
                }

                const checkout = await sdk.init({
                    sessionToken,
                    apiUrl,
                    country: languageCode?.toUpperCase() === 'NL' ? 'NL' : undefined,
                });

                // Handlers MUST be assigned here, not passed to init(). init() destructures a fixed
                // list of config keys (sessionToken, apiUrl, country, language, excludedMethods,
                // applePayOptions, paypalOptions, expressButtons*, debugMode, translations) and
                // drops everything else without complaint — so handlers passed to it are silently
                // never called. The returned handle exposes an `events` setter, which is what the
                // components actually read.
                checkout.events = {
                    onSuccess: async (event) => {
                        event.resolve?.();

                        // Report the order id before handing off. pay.nl's exchange is still the
                        // authoritative path — this only covers the case where it's lost, since an
                        // embedded payment has no order id until this moment and would otherwise
                        // leave nothing for the backend to poll.
                        const reportPaid = onPaidRef.current;
                        if (reportPaid) {
                            try {
                                await reportPaid(event.orderId ?? null);
                            } catch {
                                // Never block the buyer on our own bookkeeping.
                            }
                            return;
                        }

                        // No handler wired: fall back to leaving the page. The widget may be in an
                        // iframe on the organiser's site, so this goes through the same helper the
                        // redirect flow uses rather than touching window.location directly.
                        handleRedirect(returnUrl);
                    },
                    onError: (event) => {
                        // resolve() hands control back to the component so the buyer can retry
                        // without us tearing down and re-creating the session.
                        event.resolve?.();
                        setError(event.message ?? '');
                    },
                    onPartialPaid: (event) => {
                        // A giftcard covered only part of the order. Deliberately no redirect —
                        // leaving now would strand a paid-but-incomplete order. Instead we record
                        // what is left and let the effect below offer the remaining methods.
                        event.resolve?.();
                        setError(null);
                        setRemaining({
                            amount: event.amountRemaining ?? 0,
                            currency: event.currency ?? 'EUR',
                        });
                    },
                };

                if (cancelled) return;

                checkoutRef.current = checkout;

                const paymentComponent = checkout.prepare(component);
                await paymentComponent.bind('#pay-parts-container');

                if (!cancelled) setReady(true);
            } catch (e) {
                if (cancelled) return;

                // The component refused to mount — a bad component name, a method not enabled on
                // the service location, a blocked SDK. It is entirely client-side, so the backend
                // believes the session is fine and nothing will recover it. There is also no
                // payment URL to fall back to here: returnUrl is the POST-payment page, so sending
                // the buyer there would look like success without them having paid.
                //
                // So hand back to the hosted flow instead of leaving them on a dead form. Once
                // only — if that fails too, show the error rather than loop.
                const fallBack = onMountFailedRef.current;
                if (fallBack) {
                    console.warn('PAY.PARTS component failed to mount, falling back', e);
                    fallBack();
                    return;
                }

                setError(e instanceof Error ? e.message : 'Could not start the payment');
            }
        };

        void mount();

        return () => {
            cancelled = true;
        };
    }, [sessionToken, component, sdkUrl, apiUrl, returnUrl, languageCode]);

    // Offer every other method for the remainder. Runs after render so the container exists, and
    // only once — the SDK re-renders the list itself if the outstanding amount changes again (a
    // second giftcard, say), so re-binding on every update would fight it.
    // Boolean rather than the object itself: only the transition into "something is owed"
    // should trigger a mount, not every update to the amount.
    const hasRemainder = remaining !== null;

    React.useEffect(() => {
        if (!hasRemainder) return;

        const checkout = checkoutRef.current;
        if (!checkout) return;

        let cancelled = false;

        const mountRemainder = async () => {
            try {
                // Which methods the payer is offered for the remainder is decided entirely by
                // pay.nl: we send no method scoping when starting the session, and the SDK filters
                // its checkoutOptions by excludedMethods and the amount range only. When the list
                // comes back shorter than expected, this is the only place the reason is visible.
                const config = checkout.getConfig();
                console.debug('[PAY.PARTS] remainder methods', {
                    amount: config.amount,
                    excludedMethods: config.excludedMethods,
                    checkoutOptions: config.checkoutOptions?.map((o) => `${o.tag} ${o.name}`),
                });

                const methods = checkout.prepare('methods');
                await methods.bind('#pay-parts-remainder');
            } catch (e) {
                if (!cancelled) {
                    // The giftcard leg is already authorised at this point, so falling back to a
                    // fresh hosted attempt would risk charging twice. Surface it instead and let
                    // pay.nl's exchange settle the order if the buyer completes it another way.
                    console.error('Could not offer the remaining payment methods', e);
                    setError('');
                }
            }
        };

        void mountRemainder();

        return () => {
            cancelled = true;
        };
    }, [hasRemainder]);

    return (
        <div className="flex flex-col gap-4">
            {error !== null && (
                <p className="text-sm text-red-600" role="alert">
                    {error === '' ? m.embedded_payment_error() : error}
                </p>
            )}

            {!ready && error === null && (
                <p className="text-sm opacity-70">{m.loading()}</p>
            )}

            <div ref={containerRef} id="pay-parts-container" />

            {/* A giftcard covered part of the order. The giftcard form stays mounted above (a
                second card is a valid way to finish), and every other method is offered here.
                pay.nl prices whatever is chosen at the outstanding amount, not the order total. */}
            {remaining !== null && (
                <div className="flex flex-col gap-2 border-t pt-4">
                    <p className="text-sm font-medium">
                        {m.embedded_payment_remaining({
                            amount: Money.fromInteger(remaining.amount, remaining.currency).display(languageCode ?? 'en'),
                        })}
                    </p>
                    <div id="pay-parts-remainder" />
                </div>
            )}

            {onCancel && (
                <button type="button" className="text-sm underline opacity-70" onClick={onCancel}>
                    {m.embedded_payment_change_method()}
                </button>
            )}
        </div>
    );
}
